import {
  CustomContentUIPart,
  DataUIPart,
  DynamicToolUIPart,
  FileUIPart,
  ProviderMetadata,
  ReasoningUIPart,
  SourceDocumentUIPart,
  SourceUrlUIPart,
  StepStartUIPart,
  TextUIPart,
  ToolUIPart,
  UIMessage,
} from "ai";
import { cloneOptionalJsonValue } from "@/graphql/json-value";
import {
  GraphQLStreamingState,
  GraphQLToolUseState,
  StreamingState,
  ToolUseState,
} from "../enum.types";
import { ConverterError } from "../error";
import { GraphQLUIMessage } from "../fragment.types";

type GraphQLToolUIPart = Extract<
  GraphQLUIMessage["parts"][number],
  { __typename: "ToolUIPart" }
>;
type RestoredToolUIPart = ToolUIPart | DynamicToolUIPart;
type RestoredToolApproval = {
  id: string;
  approved?: boolean;
  reason?: string;
  isAutomatic?: boolean;
  signature?: string;
};

export function uiMessageGqlToVercel(uiMessage: GraphQLUIMessage): UIMessage {
  const metadata = cloneOptionalJsonValue(uiMessage.metadata);

  return {
    id: uiMessage.id,
    role: roleGqlToVercel(uiMessage.role),
    metadata: metadata ?? undefined,
    parts: partsGqlToVercel(uiMessage.parts),
  };
}

function roleGqlToVercel(role: GraphQLUIMessage["role"]): UIMessage["role"] {
  const map = {
    ASSISTANT: "assistant",
    USER: "user",
    SYSTEM: "system",
  } as const satisfies Record<GraphQLUIMessage["role"], UIMessage["role"]>;
  return map[role];
}

function streamingStateGqlToVercel(
  state: GraphQLStreamingState | null,
): StreamingState {
  if (state === null) return undefined;
  const map = {
    STREAMING: "streaming",
    DONE: "done",
  } as const satisfies Record<GraphQLStreamingState, StreamingState>;
  return map[state];
}

function toolStateGqlToVercel(state: GraphQLToolUseState): ToolUseState {
  const map = {
    APPROVAL_REQUESTED: "approval-requested",
    APPROVAL_RESPONDED: "approval-responded",
    INPUT_AVAILABLE: "input-available",
    INPUT_STREAMING: "input-streaming",
    OUTPUT_AVAILABLE: "output-available",
    OUTPUT_DENIED: "output-denied",
    OUTPUT_ERROR: "output-error",
  } as const satisfies Record<GraphQLToolUseState, ToolUseState>;
  return map[state];
}

function partsGqlToVercel(
  parts: GraphQLUIMessage["parts"],
): UIMessage["parts"] {
  return parts.map((part) => {
    switch (part.__typename) {
      case "TextUIPart": {
        return {
          type: "text",
          text: part.text,
          state: streamingStateGqlToVercel(part.streamingState),
          providerMetadata: restoreOptionalProviderMetadata(
            part.providerMetadata,
          ) as TextUIPart["providerMetadata"],
        } satisfies TextUIPart;
      }
      case "ReasoningUIPart": {
        return {
          type: "reasoning",
          text: part.text,
          state: streamingStateGqlToVercel(part.streamingState),
          providerMetadata: restoreOptionalProviderMetadata(
            part.providerMetadata,
          ) as ReasoningUIPart["providerMetadata"],
        } satisfies ReasoningUIPart;
      }
      case "SourceUrlUIPart": {
        return {
          type: "source-url",
          url: part.url,
          title: part.sourceTitle ?? undefined,
          sourceId: part.sourceId,
          providerMetadata: restoreOptionalProviderMetadata(
            part.providerMetadata,
          ) as SourceUrlUIPart["providerMetadata"],
        } satisfies SourceUrlUIPart;
      }
      case "SourceDocumentUIPart": {
        return {
          type: "source-document",
          title: part.documentTitle,
          sourceId: part.sourceId,
          mediaType: part.mediaType,
          filename: part.filename ?? undefined,
          providerMetadata: restoreOptionalProviderMetadata(
            part.providerMetadata,
          ) as SourceDocumentUIPart["providerMetadata"],
        } satisfies SourceDocumentUIPart;
      }
      case "FileUIPart": {
        return {
          type: "file",
          filename: part.filename ?? undefined,
          mediaType: part.mediaType,
          url: part.url,
          providerMetadata: restoreOptionalProviderMetadata(
            part.providerMetadata,
          ) as FileUIPart["providerMetadata"],
        } satisfies FileUIPart;
      }
      case "DataUIPart": {
        if (part.type === "custom") {
          const data = cloneOptionalJsonValue(part.data);
          if (
            !data ||
            typeof data !== "object" ||
            Array.isArray(data) ||
            typeof data.kind !== "string" ||
            !data.kind.includes(".")
          ) {
            throw new ConverterError("Invalid custom UI part");
          }
          return {
            type: "custom",
            kind: data.kind as CustomContentUIPart["kind"],
            providerMetadata: restoreOptionalProviderMetadata(
              data.providerMetadata,
            ) as CustomContentUIPart["providerMetadata"],
          } satisfies CustomContentUIPart;
        }
        return {
          id: part.id ?? undefined,
          type: part.type as `data-${string}`,
          data: cloneOptionalJsonValue(part.data) as DataUIPart<
            Record<string, unknown>
          >["data"],
        } satisfies DataUIPart<Record<string, unknown>>;
      }
      case "ToolUIPart": {
        const state = toolStateGqlToVercel(part.toolState);
        const base = toolPartBaseGqlToVercel(part);
        const inputValue = cloneOptionalJsonValue(part.input);
        const normalizedInput = (inputValue ??
          {}) as RestoredToolUIPart["input"];
        const callProviderMetadata = restoreOptionalProviderMetadata(
          part.callProviderMetadata,
        ) as RestoredToolUIPart["callProviderMetadata"];
        const approval = restoreOptionalToolApproval(part.approval);
        const common = {
          providerExecuted: part.providerExecuted ?? undefined,
          callProviderMetadata,
        };
        switch (state) {
          case "input-streaming": {
            return {
              ...base,
              ...common,
              state: "input-streaming",
              input: normalizedInput,
            } as RestoredToolUIPart;
          }
          case "input-available": {
            return {
              ...base,
              ...common,
              state: "input-available",
              input: normalizedInput,
            } as RestoredToolUIPart;
          }
          case "approval-requested": {
            return {
              ...base,
              ...common,
              state: "approval-requested",
              input: normalizedInput,
              approval: requireToolApproval(
                approval,
                "approval-requested",
                undefined,
              ),
            } as RestoredToolUIPart;
          }
          case "approval-responded": {
            return {
              ...base,
              ...common,
              state: "approval-responded",
              input: normalizedInput,
              approval: requireToolApproval(
                approval,
                "approval-responded",
                "boolean",
              ),
            } as RestoredToolUIPart;
          }
          case "output-available": {
            const outputValue = cloneOptionalJsonValue(part.output);
            const resultProviderMetadata = restoreOptionalProviderMetadata(
              part.resultProviderMetadata,
            ) as ProviderMetadata | undefined;
            return {
              ...base,
              ...common,
              state: "output-available",
              input: normalizedInput,
              output: (outputValue ?? null) as RestoredToolUIPart["output"],
              resultProviderMetadata,
              preliminary: part.preliminary ?? undefined,
              ...(approval === undefined
                ? {}
                : {
                    approval: requireToolApproval(
                      approval,
                      "output-available",
                      true,
                    ),
                  }),
            } as RestoredToolUIPart;
          }
          case "output-error": {
            const resultProviderMetadata = restoreOptionalProviderMetadata(
              part.resultProviderMetadata,
            ) as ProviderMetadata | undefined;
            return {
              ...base,
              ...common,
              state: "output-error",
              input: normalizedInput,
              errorText: part.errorText ?? "",
              resultProviderMetadata,
              ...(approval === undefined
                ? {}
                : {
                    approval: requireToolApproval(
                      approval,
                      "output-error",
                      true,
                    ),
                  }),
            } as RestoredToolUIPart;
          }
          case "output-denied": {
            return {
              ...base,
              ...common,
              state: "output-denied",
              input: normalizedInput,
              approval: requireToolApproval(approval, "output-denied", false),
            } as RestoredToolUIPart;
          }
        }
      }
      case "StepStartUIPart": {
        return {
          type: "step-start",
        } satisfies StepStartUIPart;
      }
      default: {
        const _: never = part;
        throw new ConverterError(`Unexpected part type`);
      }
    }
  });
}

function restoreOptionalProviderMetadata(
  value: Parameters<typeof cloneOptionalJsonValue>[0],
) {
  return value === null ? undefined : cloneOptionalJsonValue(value);
}

function restoreOptionalToolApproval(
  approval: GraphQLToolUIPart["approval"],
): RestoredToolApproval | undefined {
  if (approval === null) return undefined;

  return {
    id: approval.id,
    ...(approval.approved === null ? {} : { approved: approval.approved }),
    ...(approval.reason === null ? {} : { reason: approval.reason }),
    ...(approval.isAutomatic === null
      ? {}
      : { isAutomatic: approval.isAutomatic }),
    ...(approval.signature === null ? {} : { signature: approval.signature }),
  };
}

function requireToolApproval(
  approval: RestoredToolApproval | undefined,
  state: ToolUseState,
  expectedApproved: boolean | "boolean" | undefined,
): RestoredToolApproval {
  if (approval === undefined) {
    throw new ConverterError(`Tool part in state ${state} is missing approval`);
  }

  if (
    expectedApproved === "boolean" &&
    typeof approval.approved !== "boolean"
  ) {
    throw new ConverterError(
      `Tool part in state ${state} is missing approval.approved`,
    );
  }

  if (expectedApproved === undefined && approval.approved !== undefined) {
    throw new ConverterError(
      `Tool part in state ${state} must not include approval.approved`,
    );
  }

  if (
    typeof expectedApproved === "boolean" &&
    approval.approved !== expectedApproved
  ) {
    throw new ConverterError(
      `Tool part in state ${state} requires approval.approved=${expectedApproved}`,
    );
  }

  return approval;
}

function toolPartBaseGqlToVercel(part: GraphQLToolUIPart) {
  if (part.toolType === "dynamic-tool") {
    if (!part.toolName) {
      throw new ConverterError("dynamic-tool part is missing toolName");
    }
    if (!part.toolCallId) {
      throw new ConverterError("dynamic-tool part is missing toolCallId");
    }
    return {
      type: "dynamic-tool" as const,
      toolName: part.toolName,
      toolCallId: part.toolCallId,
    };
  }

  return {
    toolCallId: part.toolCallId ?? undefined,
    type: part.toolType as `tool-${string}`,
  };
}
